import { LocationTypeDisplayId } from "../schema"

import { Action, createAsyncThunk } from "@reduxjs/toolkit"
import { Dispatch } from "react"
import { getBaseEndpoint } from "../../../features/endpoints/endpointManager"
import axios, { AxiosError, AxiosResponse } from "axios"
import {
  LocationPhoto,
  Location,
  PracticePhoneTypeDisplayId,
  VirtualLocation,
  Practice,
} from "../schema"
import { blobToBase64, formatPhoneNumber } from "../../../features/shared/utils"
import { practiceLoaderSlice } from "./loader"
import { RootState } from "../../../reduxStore/store"
import { practiceSlice, updateSelectedLocation } from "../model"
import { cloneDeep } from "lodash"
import { DataStatus } from "../../enums"
import { selectContent } from "../../CMS/model"
import { errorHandlerUpdated } from "../../../features/errors/errorSlice"
import appInsightsLogger from "../../../features/logging/appInsightsLogger"
import { getUniqueFullUserNamesMap } from "../../Account/helpers"
import {
  getBase64ImageFromStorage,
  saveBase64ImageWithEviction,
} from "../../../features/shared/localStorageBase64"

const getLocationPhotosEndpoint = (
  locationId: string,
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/locations/${locationId}/photos?practiceId=${practiceId}`
  )
}
const getLocationPhotoThumbnailEndpoint = (
  photoId: string,
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/locationPhoto/${photoId}/thumbnail?practiceId=${practiceId}`
  )
}
const getLocationsEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/locations`
}
const getUpdateLocationEndpoint = (
  practiceId: string,
  locationId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/practices/${practiceId}/locations/${locationId}`
  )
}
const getVirtualLocationEndpoint = (practiceId: string): string => {
  return getBaseEndpoint() + `/practice/practices/${practiceId}/virtuallocation`
}

///////////////////
// VIRTUAL PHOTO //
///////////////////

export interface GetLocationPhotosArgs {
  practiceId: string
  locationId: string
  setDataStatus?: (dataStatus: DataStatus) => void
}
export const getLocationPhotosThunk = createAsyncThunk(
  "practiceProfile/locationPhoto/getLocationPhotos",
  async (
    { practiceId, locationId, setDataStatus }: GetLocationPhotosArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const haAdminText = state.contentManagement.content?.common?.haAdmin
    const userIdsToHydrate: (string | undefined)[] = []

    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationPhotosGetDataStatus: DataStatus.SENDING,
      }),
    )
    setDataStatus?.(DataStatus.SENDING)

    try {
      const url: string = getLocationPhotosEndpoint(locationId, practiceId)

      const response = await axios.get(url)
      if (response.status === 200) {
        const data = response.data
        let locationPhotos: LocationPhoto[] = await Promise.all(
          data.map(async (locationPhoto: LocationPhoto) => {
            locationPhoto.fileStatus = "Unknown"
            const url: string = getLocationPhotoThumbnailEndpoint(
              locationPhoto.id,
              practiceId,
            )
            try {
              // check local storage for base64 string before making network call
              const cachedBase64 = getBase64ImageFromStorage(locationPhoto.id)
              if (cachedBase64) {
                locationPhoto.fileStatus = "Available"
                locationPhoto.base64Thumbnail = cachedBase64
                userIdsToHydrate.push(locationPhoto.createdBy)
                return {
                  ...locationPhoto,
                }
              }

              // if not in cache, make network call to fetch thumbnail and then save to cache
              const response = await axios.get(url, { responseType: "blob" })
              if (response.status === 200) {
                locationPhoto.fileStatus = "Available"
                const fileBlob: Blob = response.data
                const base64String = await blobToBase64(fileBlob)
                // save to local storage
                saveBase64ImageWithEviction(locationPhoto.id, base64String)
                // stitch base64 string into location photo for immediate display
                locationPhoto.base64Thumbnail = base64String
                // stitch createdByFullName into location photo
                userIdsToHydrate.push(locationPhoto.createdBy)
              } else if (response.status === 202) {
                locationPhoto.fileStatus = "Processing"
              }
              return {
                ...locationPhoto,
              }
            } catch (error) {
              console.error(error)
              // Delete any quarantined files found
              if (error && (error as AxiosError).response?.status === 410) {
                const url: string = deleteLocationPhotoEndpoint(
                  locationPhoto.id,
                  practiceId,
                )
                await axios.delete(url)
                return null
              }
              throw error
            }
          }),
        )

        // Filter removed files due to quarantine if any
        locationPhotos = locationPhotos.filter(
          (locationPhoto) => locationPhoto !== null,
        )

        // hydrate all user full names at once
        const userIds = userIdsToHydrate.filter(
          (userId): userId is string => userId !== undefined,
        )
        const userIdToFullNameMap = await getUniqueFullUserNamesMap(
          userIds,
          haAdminText,
        )

        // stitch full names into location photos
        locationPhotos = locationPhotos.map((locationPhoto) => {
          return {
            ...locationPhoto!,
            createdByFullName:
              locationPhoto!.createdBy !== undefined
                ? userIdToFullNameMap[locationPhoto!.createdBy]
                : undefined,
          }
        })

        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locationPhotos: locationPhotos,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotosGetDataStatus: DataStatus.LOADED,
          }),
        )
        setDataStatus?.(DataStatus.LOADED)
      } else {
        throw new Error(
          "Failed to fetch office location photos from practice service",
        )
      }
    } catch (error) {
      console.error(error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPhotosGetDataStatus: DataStatus.ERROR,
        }),
      )
      setDataStatus?.(DataStatus.ERROR)
    }
  },
)

export interface GetLocationPhotoArgs {
  practiceId: string
  photoId: string
  setDataStatus?: (dataStatus: DataStatus) => void
}
export interface LocationImage {
  id: string
  fileStatus: string
}
export const getLocationPhotoThunk = createAsyncThunk(
  "practiceProfile/locationPhoto/getLocationPhoto",
  async (
    { practiceId, photoId, setDataStatus }: GetLocationPhotoArgs,
    { dispatch, getState },
  ) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationPhotoGetDataStatus: DataStatus.SENDING,
      }),
    )
    const locationImage: LocationImage = { id: photoId, fileStatus: "" }
    try {
      setDataStatus?.(DataStatus.SENDING)

      const url: string = getLocationPhotoThumbnailEndpoint(photoId, practiceId)
      const response = await axios.get(url, { responseType: "blob" })
      if (response.status === 200 || response.status === 202) {
        if (response.status === 200) {
          locationImage.fileStatus = "Available"
          const fileBlob: Blob = response.data
          const base64String = await blobToBase64(fileBlob)
          // save to local storage
          saveBase64ImageWithEviction(locationImage.id, base64String)
        } else {
          locationImage.fileStatus = "Processing"
        }
        const state = getState() as RootState
        const index = state.practice.practice.locationPhotos.findIndex(
          (photo) => photo.id === locationImage.id,
        )
        const newLocationPhotos = state.practice.practice.locationPhotos.map(
          (photo, i) =>
            i === index
              ? { ...photo, fileStatus: locationImage.fileStatus }
              : photo,
        )
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locationPhotos: newLocationPhotos,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotoGetDataStatus: DataStatus.LOADED,
          }),
        )
        setDataStatus?.(DataStatus.LOADED)
      } else {
        throw new Error(
          "Failed to fetch office location photo from practice service",
        )
      }
    } catch (error) {
      // catch 410 axios error
      if (error && (error as AxiosError).response?.status === 410) {
        const state = getState() as RootState
        const cmsContent = selectContent(state)
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: cmsContent?.liability?.actionNeeded || "",
              content: cmsContent?.liability?.malwareInsuranceUploadError || "",
            },
          }),
        )

        const url: string = deleteLocationPhotoEndpoint(photoId, practiceId)
        await axios.delete(url)
        locationImage.fileStatus = "Quarantined"
        const newLocationPhotos = state.practice.practice.locationPhotos.filter(
          (photo) => photo.id !== locationImage.id,
        )
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locationPhotos: newLocationPhotos,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotoGetDataStatus: DataStatus.LOADED,
          }),
        )
        setDataStatus?.(DataStatus.LOADED)
        return
      }
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPhotosGetDataStatus: DataStatus.ERROR,
        }),
      )
      setDataStatus?.(DataStatus.ERROR)
      console.error(error)
    }
  },
)

export interface LocationPhotoDto {
  id: string
  locationPhotoTypeDisplayId: string
  name: string
  description: string
  fileName: string
  fileId: string
  thumbnailFieldId: string
  lastUpdated: string
  createdBy?: string
  createdDateTime?: string
}
interface PostLocationPhotoThunkArgs {
  practiceId: string
  locationId: string
  payload: {
    locationPhotoType: string
    name: string
    description: string
    file: Blob | null
    fileName: string
  }
}
export const postLocationPhotoThunk = createAsyncThunk(
  "practiceProfile/locationPhoto/postLocationPhoto",
  async (
    { practiceId, locationId, payload }: PostLocationPhotoThunkArgs,
    { dispatch, getState },
  ) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationPhotoPostDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const formData = new FormData()
      formData.append("LocationPhotoTypeDisplayId", payload.locationPhotoType)
      formData.append("Name", payload.name.toString())
      formData.append("Description", payload.description.toString())

      if (payload.file) {
        formData.append("File", payload.file, payload.fileName)
      }

      const url: string = getLocationPhotosEndpoint(locationId, practiceId)
      const response = await axios.post(url, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
      if (response.status === 201) {
        // Add new photo information
        const state = getState() as RootState
        const newLocationPhotos = [...state.practice.practice.locationPhotos]
        newLocationPhotos.push(response.data)
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locationPhotos: newLocationPhotos,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))

        // Download thumbnail image
        // Will likely return a "Processing" fileState so we will need to poll until "Available"
        dispatch(
          getLocationPhotoThunk({
            practiceId,
            photoId: response.data.id,
          }),
        )
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotoPostDataStatus: DataStatus.LOADED,
          }),
        )
        return response.data as LocationPhotoDto
      }
    } catch (error) {
      // Handle upload errors
      appInsightsLogger.traceError(error)
      const state = getState() as RootState
      const cmsContent = selectContent(state)
      if (axios.isAxiosError(error) && error.response?.status === 422) {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: cmsContent?.common?.fileUploadErrorTitle || "",
              content:
                cmsContent?.common
                  ?.errorBannerIncompatibleFileUploadErrorContent || "",
            },
          }),
        )
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotoPostDataStatus: DataStatus.LOADED,
          }),
        )
        return
      } else if (axios.isAxiosError(error) && error.response?.status === 500) {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title:
                cmsContent?.common?.errorBannerInternalServerErrorTitle || "",
              content:
                cmsContent?.common?.errorBannerInternalServerErrorContent || "",
            },
          }),
        )
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationPhotoPostDataStatus: DataStatus.LOADED,
          }),
        )
        return
      }

      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPhotoPostDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

const deleteLocationPhotoEndpoint = (
  photoId: string,
  practiceId: string,
): string => {
  return (
    getBaseEndpoint() +
    `/practice/locationPhoto/${photoId}?practiceId=${practiceId}`
  )
}
export const deleteLocationPhotoThunk = createAsyncThunk(
  "practiceProfile/locationPhoto/deleteLocationPhoto",
  async (
    { practiceId, photoId, setDataStatus }: GetLocationPhotoArgs,
    { dispatch, getState },
  ) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationPhotoDeleteDataStatus: DataStatus.SENDING,
      }),
    )
    setDataStatus?.(DataStatus.SENDING)

    try {
      const url: string = deleteLocationPhotoEndpoint(photoId, practiceId)
      await axios.delete(url)
      const state = getState() as RootState
      const newLocationPhotos = [...state.practice.practice.locationPhotos]
      const locationIndexToDelete = newLocationPhotos.findIndex(
        (photo) => photo.id === photoId,
      )
      if (locationIndexToDelete !== -1) {
        newLocationPhotos.splice(locationIndexToDelete, 1)
      }
      const locationToUpdate = state.practice.practice.locations.find(
        (location) => location.photos?.some((photo) => photo.id === photoId),
      )
      const newLocations = state.practice.practice.locations.map((location) => {
        if (location.id === locationToUpdate?.id) {
          const newPhotos = location.photos?.filter(
            (photo) => photo.id !== photoId,
          )
          return {
            ...location,
            photos: newPhotos,
          }
        }
        return location
      })
      const updatedPractice: Practice = cloneDeep({
        ...state.practice.practice,
        locationPhotos: newLocationPhotos,
        locations: newLocations,
      })
      dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPhotoDeleteDataStatus: DataStatus.LOADED,
        }),
      )
      setDataStatus?.(DataStatus.LOADED)
      return true
    } catch (error) {
      console.error(error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPhotoDeleteDataStatus: DataStatus.ERROR,
        }),
      )
      setDataStatus?.(DataStatus.ERROR)
      return false
    }
  },
)

///////////////
// LOCATIONS //
///////////////

export const getLocationsThunk = createAsyncThunk(
  "practiceProfile/location/getLocations",
  async (practiceId: string, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationsGetDataStatus: DataStatus.SENDING,
      }),
    )
    try {
      const url: string = getLocationsEndpoint(practiceId)
      const response = await axios.get(url)
      if (response.status === 200) {
        const data = response.data
        // TODO: NF (5/6/24) Refactor location/virtual location model into a single locations model
        // We should be using this endpoint for BOTH virtual and physical locations but were not.
        // Until we refactor the location model to support both physical and virtual locations,
        // we will use this endpoint for ONLY physical locations.
        // const locations: Location[] = data.map((location: Location) => location)
        const physicalLocations = data.filter(
          (location: Location) =>
            location.locationTypeDisplayId !== LocationTypeDisplayId.VIRTUAL,
        )
        const state = getState() as RootState
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locations: physicalLocations,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationsGetDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          "Failed to fetch office locations from practice service",
        )
      }
    } catch (error) {
      // 404 here means practice with practiceId doesn't exist. Should be impossible.
      // Empty locations returns 200, with [] body.
      console.error(error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationsGetDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

export interface UpdateLocationArgs {
  practiceId: string
  data: Location
  setUpdateLocationDataStatus?: (status: DataStatus) => void
}
export const updateLocationThunk = createAsyncThunk(
  "practiceProfile/location/updateLocation",
  async (
    { practiceId, data, setUpdateLocationDataStatus }: UpdateLocationArgs,
    { dispatch, getState },
  ): Promise<Location | void> => {
    setUpdateLocationDataStatus?.(DataStatus.SENDING)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        locationPostPutDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let response: AxiosResponse<Location, any>
      if (data.id) {
        // Modifying existing location.
        const url = getUpdateLocationEndpoint(practiceId, data.id)
        response = await axios.put(url, data)
      } else {
        // Create new location.
        const url = getLocationsEndpoint(practiceId)
        response = await axios.post(url, data)
      }
      const state = getState() as RootState
      const previousPrimaryLocationId = state.practice.practice.locations.find(
        (location) => location.isPrimary,
      )?.id
      const updateLocationId = response.data.id
      const updatedLocation = response.data
      const updatedLocations = [...state.practice.practice.locations]
      const index = updatedLocations.findIndex((x: Location) => {
        return x.id === updateLocationId
      })
      if (index !== -1) {
        updatedLocations.splice(index, 1)
      }
      updatedLocations.push(updatedLocation)
      const updatedPractice: Practice = cloneDeep({
        ...state.practice.practice,
        locations: updatedLocations,
      })
      dispatch(updateSelectedLocation(updatedLocation))
      dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))

      setUpdateLocationDataStatus?.(DataStatus.LOADED)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPostPutDataStatus: DataStatus.SAVED,
        }),
      )

      // is primary location was changed, refetch locations
      if (
        previousPrimaryLocationId !== updateLocationId &&
        updatedLocation.isPrimary
      ) {
        await dispatch(getLocationsThunk(practiceId))
      }

      return response.data
    } catch (error) {
      console.error(error)
      setUpdateLocationDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationPostPutDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

const handleDeleteLocationConflict = (
  response: AxiosResponse,
  dispatch: Dispatch<Action>,
  getState: () => unknown,
) => {
  const providerCount = response.data?.providerCount
  const state = getState() as RootState
  const locationListItemTexts =
    state?.contentManagement?.content?.locationListItem
  dispatch(
    errorHandlerUpdated({
      errorBanner: {
        isVisible: true,
        title: "Error",
        content:
          (providerCount == 1
            ? locationListItemTexts?.deleteHasProviderAssociatedError
            : locationListItemTexts?.deleteHasProvidersAssociatedError.replace(
                "{providerCount}",
                String(providerCount),
              )) ?? "",
      },
    }),
  )
  return
}

export interface DeleteLocationArgs {
  practiceId: string
  locationId: string
}
export const deleteLocationThunk = createAsyncThunk(
  "practiceProfile/location/deleteLocation",
  async (
    { practiceId, locationId }: DeleteLocationArgs,
    { dispatch, getState },
  ): Promise<void> => {
    try {
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          locationDeleteDataStatus: DataStatus.SENDING,
        }),
      )
      const url = getUpdateLocationEndpoint(practiceId, locationId)
      const response = await axios.delete(url, {
        validateStatus: function (status) {
          return status === 204 || status === 409
        },
      })
      if (response.status === 204) {
        // get state
        const state = getState() as RootState
        // get locations from state, remove location from locations list where id === locationId, call locations updated
        const newLocations = state.practice.practice.locations.filter(
          (location) => location.id !== locationId,
        )
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          locations: newLocations,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationDeleteDataStatus: DataStatus.SAVED,
          }),
        )
      } else if (response.status === 409) {
        handleDeleteLocationConflict(response, dispatch, getState)
        return
      } else {
        dispatch(
          practiceLoaderSlice.actions.practiceDataStatusUpdated({
            locationDeleteDataStatus: DataStatus.ERROR,
          }),
        )
        throw new Error(
          "Failed to delete office location from practice service",
        )
      }
    } catch (error) {
      console.error(error)
    }
  },
)

///////////////////////
// VIRTUAL LOCATIONS //
///////////////////////

export const getVirtualOfficeInformationThunk = createAsyncThunk(
  "practiceProfile/virtualLocation/getVirtualOfficeInformation",
  async (practiceId: string, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        virtualLocationGetDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      // will need to be updated to use any main practice contact location (not just virtual location)
      const url: string = getVirtualLocationEndpoint(practiceId)
      const response = await axios.get(url, {
        validateStatus: function (status) {
          return status === 200 || status === 404
        },
      })

      let virtualLocation: VirtualLocation | null = null
      if (response.status === 200) {
        const data = response.data
        virtualLocation = {
          id: data.id,
          phoneNumber: formatPhoneNumber(data.phoneNumber),
          emailAddress: data.emailAddress,
          phoneType: data.phoneType as PracticePhoneTypeDisplayId,
          faxNumber: formatPhoneNumber(data.faxNumber),
          createdBy: data.createdBy,
          locationTypeDisplayId: LocationTypeDisplayId.VIRTUAL,
          officeRequirementsCertified: data.officeRequirementsCertified,
        }
      }
      const state = getState() as RootState
      const updatedPractice: Practice = cloneDeep({
        ...state.practice.practice,
        virtualLocation: virtualLocation,
      })
      dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationGetDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      console.error(error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationGetDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

export const deleteVirtualOfficeInformationThunk = createAsyncThunk(
  "practiceProfile/virtualLocation/deleteVirtualOfficeInformation",
  async (practiceId: string, { dispatch, getState }) => {
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        virtualLocationDeleteDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      // will need to be updated to use any main practice contact location (not just virtual location)
      const url: string = getVirtualLocationEndpoint(practiceId)
      const response = await axios.delete(url, {
        validateStatus: function (status) {
          return status === 204 || status === 404 || status === 409
        },
      })

      if (response.status === 409) {
        handleDeleteLocationConflict(response, dispatch, getState)
        return
      }

      const state = getState() as RootState
      const updatedPractice: Practice = cloneDeep({
        ...state.practice.practice,
        virtualLocation: null,
      })
      dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationDeleteDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      console.error(error)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationDeleteDataStatus: DataStatus.ERROR,
        }),
      )
    }
  },
)

// TODO: type the request and response
interface SetVirtualLocationDataArgs {
  practiceId: string
  contactInformation: VirtualLocation
  setOfficeInformationDataStatus?: (status: DataStatus) => void
}
export const setOfficeInformationThunk = createAsyncThunk(
  "practiceProfile/virtualLocation/setOfficeInformation",
  async (
    {
      practiceId,
      contactInformation,
      setOfficeInformationDataStatus,
    }: SetVirtualLocationDataArgs,
    { dispatch, getState },
  ) => {
    setOfficeInformationDataStatus?.(DataStatus.SENDING)
    dispatch(
      practiceLoaderSlice.actions.practiceDataStatusUpdated({
        virtualLocationPutDataStatus: DataStatus.SENDING,
      }),
    )

    try {
      const url: string = getVirtualLocationEndpoint(practiceId)
      const payload = {
        ...contactInformation,
      }
      const response = await axios.put(url, payload, {
        validateStatus: function (status) {
          return status === 200 || status === 201
        },
      })

      const data = response.data
      const virtualLocation: VirtualLocation = {
        id: data.id,
        phoneNumber: formatPhoneNumber(data.phoneNumber),
        emailAddress: data.emailAddress,
        phoneType: data.phoneType as PracticePhoneTypeDisplayId,
        faxNumber: formatPhoneNumber(data.faxNumber),
        locationTypeDisplayId: LocationTypeDisplayId.VIRTUAL,
        officeRequirementsCertified: data.officeRequirementsCertified,
      }
      const state = getState() as RootState
      const updatedPractice: Practice = cloneDeep({
        ...state.practice.practice,
        virtualLocation: virtualLocation,
      })
      dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))

      setOfficeInformationDataStatus?.(DataStatus.LOADED)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationPutDataStatus: DataStatus.LOADED,
        }),
      )
    } catch (error) {
      setOfficeInformationDataStatus?.(DataStatus.ERROR)
      dispatch(
        practiceLoaderSlice.actions.practiceDataStatusUpdated({
          virtualLocationPutDataStatus: DataStatus.ERROR,
        }),
      )
      console.error(error)
    }
  },
)
